import { Stack } from '@mui/material';

import { type HandoverSpec } from '@/designs-dev/handover/types';
import Note from '@/designs-dev/annotations/Note';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import PlaceholderScreen from '@/karehero-app/tell-us-once/intake/PlaceholderScreen';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import {
  BAR_1_0,
  CALCULATOR_1_0,
  CALCULATOR_1_0_COLD,
  CALCULATOR_1_0_NOT_QUITE,
  CALCULATOR_1_0_RECEIVING,
  CALCULATOR_1_0_SLOW,
  PLACEHOLDER_1_0,
} from '@/karehero-app/tell-us-once/benefits/calculator-1-0.content';

const noop = () => {};

// The calculator's bar on every screen inside it: full screen, with a way out.
const bar = <CalculatorBar content={BAR_1_0} back />;

/** Where a question sits in a conversation, by id, so steps survive reorders. */
const at = (questions: ChatQuestion[], id: string) => {
  const i = questions.findIndex((q) => q.id === id);
  if (i < 0) throw new Error(`No calculator question called ${id}`);
  return i;
};

// Drawn in onboarding 4's chat, so the calculator is the same conversation
// patterns with its own bar.
const chat = (
  questions: ChatQuestion[],
  startAt = 0,
  extra: Partial<Parameters<typeof ChatV2>[0]> = {},
) => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <ChatV2
      questions={questions}
      startAt={startAt}
      onExit={noop}
      bar={extra.bar ?? bar}
      inlineMic
      liveVoice
      {...extra}
    />
  </IntakeStyleContext.Provider>
);

/** The 1.0 draft's chat from the start, for other pages that show it. */
export const calculatorChat = () => chat(CALCULATOR_1_0);

const main = (id: string, extra?: Partial<Parameters<typeof ChatV2>[0]>) =>
  chat(CALCULATOR_1_0, at(CALCULATOR_1_0, id), extra);

/** The 1.0 draft's chat at the results message, for other pages. */
export const calculatorResults = () => main('results');

/**
 * DRAFT. The benefits calculator as the first config in the shared chat
 * engine. Main's question set and "only if" rules, asked in onboarding 4's
 * chat, skipping what onboarding already holds, and ending in the profile.
 */
export const benefitsCalculator10Spec: HandoverSpec = {
  id: 'benefits-calculator-1-0',
  title: 'Benefits calculator',
  description: '',
  canvas: true,
  links: [
    {
      label: 'Dynamic flows: approach (Notion)',
      url: 'https://app.notion.com/p/karehero/Dynamic-flows-approach-3fb63e8971dd8077b6b7e39eb33b02f7',
    },
    {
      label: 'Interaction principles (Notion)',
      url: 'https://app.notion.com/p/karehero/Dynamic-flows-interaction-principles-for-the-calculator-40863e8971dd81c7b0a3c44a378fff50',
    },
    {
      label: 'Dynamic flows, in slices',
      url: '/tell-us-once/flows/dynamic-flow-slices',
    },
  ],
  flows: [
    {
      title: 'If we already hold some of it',
      steps: [
        {
          // From Home, led by the calculator in Home's lead card. Getting here
          // from onboarding is for a later iteration: onboarding lands in the
          // app, never straight into another chat.
          id: 'home',
          title: 'From Home',
          render: () => <PlaceholderScreen {...PLACEHOLDER_1_0.home} />,
        },
        {
          id: 'held',
          title: 'What it is for, and what we hold',
          render: () => main('held'),
        },
        {
          id: 'nursing-home',
          title: 'Asked because they live in England',
          render: () => main('care-recipient-in-nursing-home'),
        },
        {
          id: 'receiving',
          title: 'Five answers, behind See options',
          render: () => main('care-recipient-already-receiving-benefits'),
          alt: {
            // They might get more than one, so they tick each and Done sends
            // them together. None of these and Not sure answer on their own.
            title: 'Open: tick each one, then Done',
            render: () =>
              chat(
                CALCULATOR_1_0_RECEIVING,
                at(
                  CALCULATOR_1_0_RECEIVING,
                  'care-recipient-already-receiving-benefits',
                ),
                { optionsOpen: true },
              ),
          },
        },
        {
          id: 'carer',
          title: 'The carer',
          render: () => main('care-recipient-has-unpaid-carer'),
        },
        {
          id: 'money',
          title: 'Money, rough is fine',
          render: () => main('care-recipient-savings-bracket'),
        },
        {
          id: 'health',
          title: 'Health, last',
          render: () => main('care-recipient-is-disabled'),
        },
        {
          id: 'working-out',
          title: 'Working out what they could get',
          render: () =>
            main('care-recipient-home-requires-adaptations', { reading: true }),
        },
        {
          // How many, and the one to start with, before the app shows them.
          id: 'results-said',
          title: 'What it found, said',
          render: () => main('results'),
        },
        {
          id: 'result',
          title: 'Results',
          render: () => <PlaceholderScreen {...PLACEHOLDER_1_0.results} />,
        },
      ],
    },
    {
      title: 'If we hold nothing',
      steps: [
        {
          id: 'cold-location',
          title: 'Nothing to check, straight to where they live',
          render: () => chat(CALCULATOR_1_0_COLD),
        },
        {
          id: 'cold-age',
          title: 'Then how old',
          render: () =>
            chat(
              CALCULATOR_1_0_COLD,
              at(CALCULATOR_1_0_COLD, 'care-recipient-age-bracket'),
            ),
        },
        {
          id: 'cold-help',
          title: 'Then when they need help, and on as above',
          render: () =>
            chat(
              CALCULATOR_1_0_COLD,
              at(CALCULATOR_1_0_COLD, 'care-recipient-needs-help-time-of-day'),
            ),
        },
      ],
    },
    {
      title: 'Not quite',
      steps: [
        {
          id: 'fix-say',
          title: 'They say what is wrong',
          render: () =>
            chat(
              CALCULATOR_1_0_NOT_QUITE,
              at(CALCULATOR_1_0_NOT_QUITE, 'held-fix'),
            ),
        },
        {
          id: 'fix-again',
          title: 'Said back again, changed',
          render: () =>
            chat(
              CALCULATOR_1_0_NOT_QUITE,
              at(CALCULATOR_1_0_NOT_QUITE, 'held-again'),
            ),
        },
      ],
    },
    {
      title: 'If they already get more than one',
      steps: [
        {
          // Ticked, not sent: Done sends them together.
          id: 'receiving-ticked',
          title: 'Tick each one, then Done',
          render: () =>
            chat(
              CALCULATOR_1_0_RECEIVING,
              at(
                CALCULATOR_1_0_RECEIVING,
                'care-recipient-already-receiving-benefits',
              ),
              { optionsOpen: true },
            ),
        },
        {
          id: 'pip-part',
          title: 'Which part, asked because they get PIP',
          render: () =>
            chat(
              CALCULATOR_1_0_RECEIVING,
              at(CALCULATOR_1_0_RECEIVING, 'care-recipient-pip-part'),
            ),
        },
      ],
    },
    {
      // One voice button: the mic talks, their words show as they go and
      // send on a pause. Stop, where the mic was, puts them in the box to
      // edit first. No waveform: the people using this won't know it.
      title: 'Talking',
      steps: [
        {
          id: 'talk-start',
          title: 'Just the mic',
          render: () => main('care-recipient-unpaid-carer-35-hours-or-more'),
          alt: {
            title: 'How to use',
            render: () =>
              main('care-recipient-unpaid-carer-35-hours-or-more', {
                howToOpen: true,
              }),
          },
        },
        {
          id: 'talk-listening',
          title: 'Their words as they talk',
          render: () =>
            main('care-recipient-unpaid-carer-35-hours-or-more', {
              live: true,
            }),
          alt: {
            title: 'Stopped, the words to edit',
            render: () =>
              main('care-recipient-unpaid-carer-35-hours-or-more', {
                typed: 'Yes, more like 40',
              }),
          },
        },
        {
          // A pause sent it. The wave lies flat while it's our turn.
          id: 'talk-our-turn',
          title: 'Our turn',
          render: () =>
            main('care-recipient-has-unpaid-carer', {
              live: true,
              reading: true,
            }),
        },
        {
          id: 'talk-options',
          title: 'Options still tap',
          render: () => main('care-recipient-savings-bracket', { live: true }),
        },
      ],
    },
    {
      // Every wait in the calculator, shortest first. Each says what it is
      // doing only when the wait is long enough to notice: the interaction
      // principles in Notion have the timings.
      title: 'Thinking states',
      arrows: false,
      note: (
        <Note title='When we start working' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Every wait starts when we send their answer to the agent, and that
              depends on how they answered.
            </span>
            <span>
              <b>Typed, or a file.</b> We start as soon as they hit send. No
              waiting to see if more is coming.
            </span>
            <span>
              <b>A second message before we reply.</b> We stop and start again
              with both, and they get one reply covering everything. No fixed
              delay, so a single message never waits.
            </span>
            <span>
              <b>Files are the exception.</b> Reading them takes longest, so we
              don't start again. Anything sent while a file is being read is
              picked up once it's done.
            </span>
            <span>
              <b>Talking.</b> It sends on a pause, or they tap stop and it goes
              in the box to edit first.
            </span>
            <span>
              <b>Never block them.</b> They can keep typing or talking while we
              work, and it folds into the same reply.
            </span>
            <span>
              <b>
                Only say what it's doing when the wait is long enough to notice.
              </b>{' '}
              No "Thinking..." with nothing behind it.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          // Under 2s. Anything more makes a quick turn feel slow.
          id: 'think-next',
          note: (
            <Note title='Timing' variant='body2'>
              Under 2s. Dots only: anything more makes a quick turn feel slow.
            </Note>
          ),
          title: 'Working out the next question: dots only',
          render: () =>
            main('care-recipient-has-unpaid-carer', { reading: true }),
        },
        {
          // 2 to 5s. Their words are already on screen as they talk, so
          // there is no separate wait for a transcript.
          id: 'think-hearing',
          note: (
            <Note title='Timing' variant='body2'>
              While they talk. Their words show as they go, so there is no wait
              for a transcript.
            </Note>
          ),
          title: 'Hearing them: words as they talk',
          render: () =>
            main('care-recipient-unpaid-carer-35-hours-or-more', {
              live: true,
            }),
        },
        {
          // 2 to 5s, after Not quite: says what it is checking.
          id: 'think-checking',
          note: (
            <Note title='Timing' variant='body2'>
              2 to 5s. Their reply greys out with a spinner while it's read, and
              a short label says what we're doing.
            </Note>
          ),
          title: 'Checking a fix: a short label',
          render: () =>
            chat(
              CALCULATOR_1_0_NOT_QUITE,
              at(CALCULATOR_1_0_NOT_QUITE, 'held-fix'),
              { reading: true },
            ),
        },
        {
          // The one wait that matters, so the one with a longer, labelled
          // state.
          id: 'think-results',
          note: (
            <Note title='Timing' variant='body2'>
              Varies. The one wait that matters, so the one with a longer,
              labelled state.
            </Note>
          ),
          title: 'Working out the results: labelled',
          render: () =>
            main('care-recipient-home-requires-adaptations', { reading: true }),
        },
        {
          // 15s or more: say so plainly, and nothing is lost.
          id: 'think-slow',
          note: (
            <Note title='Timing' variant='body2'>
              15s or more. Say so plainly and give a way on. Their answers are
              saved.
            </Note>
          ),
          title: 'Too slow: say so',
          render: () =>
            chat(CALCULATOR_1_0_SLOW, at(CALCULATOR_1_0_SLOW, 'slow')),
        },
      ],
    },
    {
      title: 'Leaving, and slow results',
      arrows: false,
      steps: [
        {
          id: 'leave',
          title: 'Back asks first',
          render: () =>
            main('care-recipient-has-partner', {
              bar: <CalculatorBar content={BAR_1_0} back initialConfirming />,
            }),
        },
        {
          id: 'slow',
          title: 'Working it out takes too long',
          render: () =>
            chat(CALCULATOR_1_0_SLOW, at(CALCULATOR_1_0_SLOW, 'slow')),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
